import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { programsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '@/components/ui/table';
import {
  BarChart3, CheckCircle, Clock, AlertTriangle,
  TrendingUp, Users, FolderKanban
} from 'lucide-react';
import { toast } from 'sonner';

export default function TaskDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await programsAPI.taskDashboard();
        setData(res.data);
      } catch { toast.error('Failed to load task dashboard'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Loading...</div>;
  if (!data) return null;

  const s = data.summary;

  return (
    <div className="space-y-5" data-testid="task-dashboard-page">
      <div>
        <h1 className="text-xl font-bold tracking-tight flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Task Dashboard</h1>
        <p className="text-xs text-muted-foreground mt-0.5">On-time vs delayed task analytics across all programs</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card><CardContent className="py-3 px-4">
          <p className="text-2xl font-bold">{s.total_tasks}</p>
          <p className="text-xs text-muted-foreground">Total Tasks</p>
        </CardContent></Card>
        <Card><CardContent className="py-3 px-4">
          <p className="text-2xl font-bold text-emerald-600">{s.completed}</p>
          <p className="text-xs text-muted-foreground">Completed</p>
        </CardContent></Card>
        <Card><CardContent className="py-3 px-4">
          <div className="flex items-center gap-1"><CheckCircle className="w-4 h-4 text-blue-500" /><p className="text-2xl font-bold text-blue-600">{s.on_time}</p></div>
          <p className="text-xs text-muted-foreground">On Time</p>
        </CardContent></Card>
        <Card><CardContent className="py-3 px-4">
          <div className="flex items-center gap-1"><Clock className="w-4 h-4 text-amber-500" /><p className="text-2xl font-bold text-amber-600">{s.delayed}</p></div>
          <p className="text-xs text-muted-foreground">Delayed</p>
        </CardContent></Card>
        <Card className="border-red-200"><CardContent className="py-3 px-4">
          <div className="flex items-center gap-1"><AlertTriangle className="w-4 h-4 text-red-500" /><p className="text-2xl font-bold text-red-600">{s.overdue}</p></div>
          <p className="text-xs text-muted-foreground">Overdue</p>
        </CardContent></Card>
        <Card><CardContent className="py-3 px-4">
          <div className="flex items-center gap-1"><TrendingUp className="w-4 h-4 text-emerald-500" /><p className="text-2xl font-bold text-emerald-700">{s.on_time_rate}%</p></div>
          <p className="text-xs text-muted-foreground">On-Time Rate</p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Delay Reasons */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-1.5"><Clock className="w-4 h-4 text-amber-600" /> Top Delay Reasons</CardTitle></CardHeader>
          <CardContent>
            {data.delay_reasons.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No delays recorded</p>
            ) : (
              <div className="space-y-2">
                {data.delay_reasons.slice(0, 10).map((r, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <p className="text-xs flex-1 truncate">{r.reason}</p>
                    <Badge variant="outline" className="text-[10px] ml-2">{r.count}x</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* By User */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-1.5"><Users className="w-4 h-4" /> By Assignee</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs">User</TableHead>
                  <TableHead className="text-xs text-right">Total</TableHead>
                  <TableHead className="text-xs text-right">Done</TableHead>
                  <TableHead className="text-xs text-right">On Time</TableHead>
                  <TableHead className="text-xs text-right">Delayed</TableHead>
                  <TableHead className="text-xs text-right">Overdue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.by_user.map((u, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs font-medium">{u.name}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{u.total}</TableCell>
                    <TableCell className="text-xs text-right font-mono text-emerald-600">{u.completed}</TableCell>
                    <TableCell className="text-xs text-right font-mono text-blue-600">{u.on_time}</TableCell>
                    <TableCell className="text-xs text-right font-mono text-amber-600">{u.delayed}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{u.overdue > 0 ? <span className="text-red-600 font-semibold">{u.overdue}</span> : <span className="text-slate-300">0</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* By Program */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-1.5"><FolderKanban className="w-4 h-4" /> By Program</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="text-xs">Program</TableHead>
                <TableHead className="text-xs text-right">Total</TableHead>
                <TableHead className="text-xs text-right">Completed</TableHead>
                <TableHead className="text-xs text-right">On Time</TableHead>
                <TableHead className="text-xs text-right">Delayed</TableHead>
                <TableHead className="text-xs text-right">Completion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.by_program.map(p => (
                <TableRow key={p.program_id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/programs/${p.program_id}`)}>
                  <TableCell className="text-xs"><span className="font-medium">{p.program_name}</span> <span className="font-mono text-muted-foreground ml-1">{p.program_number}</span></TableCell>
                  <TableCell className="text-xs text-right font-mono">{p.total}</TableCell>
                  <TableCell className="text-xs text-right font-mono text-emerald-600">{p.completed}</TableCell>
                  <TableCell className="text-xs text-right font-mono text-blue-600">{p.on_time}</TableCell>
                  <TableCell className="text-xs text-right font-mono text-amber-600">{p.delayed}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="outline" className={`text-[10px] ${
                      p.completion_pct >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      p.completion_pct >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-slate-50 text-slate-600 border-slate-200'
                    }`}>{p.completion_pct}%</Badge>
                  </TableCell>
                </TableRow>
              ))}
              {data.by_program.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-xs text-muted-foreground py-4">No programs with tasks</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delayed Tasks List */}
      {data.delayed_tasks.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-amber-600" /> Delayed Tasks ({data.delayed_tasks.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs">Task</TableHead>
                  <TableHead className="text-xs">Program</TableHead>
                  <TableHead className="text-xs">Assignee</TableHead>
                  <TableHead className="text-xs">Original Due</TableHead>
                  <TableHead className="text-xs">Current Due</TableHead>
                  <TableHead className="text-xs text-right">Delays</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.delayed_tasks.map(t => (
                  <TableRow key={t.task_id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/programs/${t.program_id}`)}>
                    <TableCell className="text-xs font-medium">{t.title}</TableCell>
                    <TableCell className="text-xs">{t.program_name}</TableCell>
                    <TableCell className="text-xs">{t.assigned_to_name || '-'}</TableCell>
                    <TableCell className="text-xs font-mono line-through text-muted-foreground">{t.original_due_date || '-'}</TableCell>
                    <TableCell className="text-xs font-mono font-semibold">{t.current_due_date || '-'}</TableCell>
                    <TableCell className="text-xs text-right font-mono text-amber-600 font-semibold">{t.delay_count}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{t.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
